import { useCallback, useEffect, useState } from "react";
import { type WorkQueue, type WorkQueueItem } from "@care-by-ella/contracts";
import { LANE_LABEL } from "@/lib/work-queue-lanes";
import { useAuth } from "@/auth/AuthContext";
import { DoctorAnswerCard } from "@/components/DoctorAnswerCard";
import { WorkItemCard } from "@/components/WorkItemCard";
import { UnauthorizedError } from "@/lib/clinician-api";
import { fetchWorkQueue } from "@/lib/work-queue-api";

// TES-289 / TES-290 — the clinician work-queue workspace. Renders the caller's
// role-scoped lanes (server-decided). Most lanes route the clinician to the
// surface that owns the action (review queue, handoffs, patient detail); the
// doctor `renewals` lane embeds the decision panel so approve/reject/dose/note
// happen in place (TES-290).

// TES-1400 — the lane names moved to lib/work-queue-lanes so the card's
// "Följ upp senare" can name the lane without importing this route.
export { LANE_LABEL } from "@/lib/work-queue-lanes";

export function WorkQueueRoute() {
  const { signOut } = useAuth();
  const [data, setData] = useState<WorkQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TES-757 — the item the server handed back after a disposal. The refreshed
  // list opens that row by itself, so "skicka och avsluta" is a loop the coach
  // stays inside instead of a round trip through the list.
  const [nextItemId, setNextItemId] = useState<string | null>(null);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const reload = useCallback(() => {
    fetchWorkQueue().then(setData).catch(handleError);
  }, [handleError]);

  useEffect(reload, [reload]);

  const totalItems =
    data?.lanes.reduce((n, l) => n + l.items.length, 0) ?? null;

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Arbetskö</h1>
        <p className="mt-xs max-w-2xl text-sm text-textSecondary">
          Din prioriterade arbetskö — allt som väntar på dig, samlat per spår.
          {totalItems !== null ? ` ${totalItems} ärenden.` : ""}
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-danger/30 bg-danger/10 p-md text-sm text-danger">
          {error}
        </div>
      ) : null}

      {data?.lanes.map((lane) => (
        <section key={lane.lane} className="flex flex-col gap-sm">
          <div className="flex items-baseline gap-sm">
            <h2 className="font-display text-lg tracking-tight">
              {LANE_LABEL[lane.lane]}
            </h2>
            <span className="text-xs text-textSecondary">
              {lane.items.length}
            </span>
          </div>

          {lane.items.length === 0 ? (
            <div className="rounded-md border border-surfaceMuted bg-surface p-md text-sm text-textSecondary">
              {lane.note ?? "Inget att göra just nu."}
            </div>
          ) : (
            <ul className="flex flex-col gap-sm">
              {lane.items.map((item) => (
                <li key={`${lane.lane}:${item.id}`}>
                  <WorkQueueRow
                    item={item}
                    autoExpand={item.id === nextItemId}
                    onDisposed={(next) => {
                      setNextItemId(next?.id ?? null);
                      reload();
                    }}
                    onUnauthorized={() => void signOut()}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

// TES-519 / TES-757 — the row is still a pointer when collapsed. Expanding it
// turns it into the workspace the coach-admin package describes: context, an
// editable reply, and the six actions, without leaving the queue.
//
// Kept as a named export because LaneQueue renders the same row on the
// doctor's per-lane pages — one row implementation, two surfaces, so the two
// cannot drift the way the queue and its badge once did.
export function WorkQueueRow({
  item,
  autoExpand,
  onDisposed,
  onUnauthorized,
}: {
  item: WorkQueueItem;
  autoExpand?: boolean;
  onDisposed?: (next: WorkQueueItem | null) => void;
  onUnauthorized?: () => void;
}) {
  // TES-1332 — her answer to the doctor's question is handled in its own row.
  if (item.kind === "doctor_question_answer") {
    return (
      <DoctorAnswerCard
        item={item}
        onDisposed={onDisposed}
        onUnauthorized={onUnauthorized}
      />
    );
  }
  return (
    <WorkItemCard
      item={item}
      autoExpand={autoExpand}
      onDisposed={onDisposed}
      onUnauthorized={onUnauthorized}
    />
  );
}
